import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trash2, Search, Save, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
    getMasterModules,
    getMasterTranslations,
    upsertMasterTranslationValue,
    deleteMasterTranslationKey,
} from '../../redux/translationSlice';
import CommonLoader from '../../components/CommonLoader';
import SearchableSelect from '../../components/common/SearchableSelect';
import useCanManage from '../../hooks/useCanManage';

// Translation master for master-data entries. Rows = translation keys under a selected
// master module, columns = languages pulled from the language master (dynamic — adding a
// new row in the language master instantly reveals a new editable column here).
const MasterDataTranslation = () => {
    const { t } = useTranslation('translationpage');
    const dispatch = useDispatch();
    // Write-side controls hide without the manage key — the API refuses
    // them anyway (hooks/useCanManage.js).
    const canManage = useCanManage('masters.manage');
    const {
        masterModules,
        masterTranslationRows,
        masterTranslationLanguages,
        masterTranslationSourceLcode,
        masterTranslationLoading,
        masterTranslationSaving,
    } = useSelector((state) => state.translation);

    const [selectedModule, setSelectedModule] = useState('');
    const [search, setSearch] = useState('');
    const [dirty, setDirty] = useState({}); // { [keyId]: { [lcode]: value } }
    const inputRefs = useRef({});

    useEffect(() => {
        dispatch(getMasterModules());
    }, [dispatch]);

    // Default to the first available module.
    useEffect(() => {
        if (!selectedModule && masterModules.length > 0) {
            setSelectedModule(masterModules[0].name);
        }
    }, [masterModules, selectedModule]);

    useEffect(() => {
        if (selectedModule) {
            dispatch(getMasterTranslations(selectedModule));
            setDirty({});
        }
    }, [dispatch, selectedModule]);

    const sourceLcode = masterTranslationSourceLcode || 'en';

    // Languages in display order: source first, then alphabetical by name.
    const orderedLanguages = useMemo(() => {
        const langs = [...masterTranslationLanguages];
        langs.sort((a, b) => {
            if (a.code === sourceLcode) return -1;
            if (b.code === sourceLcode) return 1;
            return (a.name || '').localeCompare(b.name || '');
        });
        return langs;
    }, [masterTranslationLanguages, sourceLcode]);

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return masterTranslationRows;
        return masterTranslationRows.filter((row) => {
            if (row.keyName?.toLowerCase().includes(q)) return true;
            if (row.entityId?.toLowerCase().includes(q)) return true;
            if (row.field?.toLowerCase().includes(q)) return true;
            const values = row.translations || {};
            return Object.values(values).some((v) => `${v || ''}`.toLowerCase().includes(q));
        });
    }, [masterTranslationRows, search]);

    const getCellValue = (row, lcode) => {
        const pending = dirty[row.keyId]?.[lcode];
        if (pending !== undefined) return pending;
        return row.translations?.[lcode] ?? '';
    };

    const handleCellChange = (keyId, lcode, value) => {
        setDirty((prev) => ({
            ...prev,
            [keyId]: { ...(prev[keyId] || {}), [lcode]: value },
        }));
    };

    const isCellDirty = (keyId, lcode) => dirty[keyId]?.[lcode] !== undefined;

    const handleSaveCell = async (row, lcode) => {
        if (lcode === sourceLcode) return;
        const value = getCellValue(row, lcode);
        const original = row.translations?.[lcode] ?? '';
        if (value === original) {
            // Nothing changed — just clear dirty.
            setDirty((prev) => {
                const copy = { ...prev };
                if (copy[row.keyId]) {
                    delete copy[row.keyId][lcode];
                    if (Object.keys(copy[row.keyId]).length === 0) delete copy[row.keyId];
                }
                return copy;
            });
            return;
        }

        try {
            await dispatch(upsertMasterTranslationValue({
                keyId: row.keyId,
                lcode,
                value,
                moduleName: selectedModule,
                entityId: row.entityId,
                field: row.field,
            })).unwrap();
            toast.success(t('translationpage.saved', 'Saved'));
            setDirty((prev) => {
                const copy = { ...prev };
                if (copy[row.keyId]) {
                    delete copy[row.keyId][lcode];
                    if (Object.keys(copy[row.keyId]).length === 0) delete copy[row.keyId];
                }
                return copy;
            });
        } catch (err) {
            toast.error(err?.error || t('translationpage.save_failed', 'Failed to save'));
        }
    };

    const handleKeyDown = (e, row, lcode) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSaveCell(row, lcode);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setDirty((prev) => {
                const copy = { ...prev };
                if (copy[row.keyId]) {
                    delete copy[row.keyId][lcode];
                    if (Object.keys(copy[row.keyId]).length === 0) delete copy[row.keyId];
                }
                return copy;
            });
            const input = inputRefs.current[`${row.keyId}-${lcode}`];
            if (input) input.blur();
        }
    };

    const handleDeleteKey = async (row) => {
        const result = await Swal.fire({
            title: t('translationpage.delete_confirm_title', 'Delete translation key?'),
            text: t('translationpage.delete_confirm_text', 'All language values for this key will be removed. This cannot be undone.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.delete', 'Delete'),
            cancelButtonText: t('common.cancel', 'Cancel'),
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
        });
        if (!result.isConfirmed) return;

        try {
            await dispatch(deleteMasterTranslationKey({
                keyId: row.keyId,
                moduleName: selectedModule,
                entityId: row.entityId,
                field: row.field,
            })).unwrap();
            toast.success(t('translationpage.deleted', 'Deleted'));
        } catch (err) {
            toast.error(err?.error || t('translationpage.delete_failed', 'Failed to delete'));
        }
    };

    const handleRefresh = () => {
        if (selectedModule) {
            dispatch(getMasterTranslations(selectedModule));
            setDirty({});
        }
    };

    const selectedModuleMeta = masterModules.find((m) => m.name === selectedModule);

    return (
        <div className="w-full" data-testid="master-data-translation-container">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-4" data-testid="master-data-translation-header">
                <div>
                    <h2 className="text-xl font-bold text-gray-800" data-testid="master-data-translation-title">
                        {t('translationpage.master_data_tab', 'Master Data Translations')}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-testid="master-data-translation-description">
                        {t(
                            'translationpage.master_data_description',
                            'Translate master records created elsewhere in the admin panel. The source language column is read-only.'
                        )}
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto" data-testid="master-data-translation-controls">
                    <div data-testid="master-data-translation-module-field-container">
                        <label className="block text-xs font-semibold text-gray-600 mb-1" data-testid="master-data-translation-module-label">
                            {t('translationpage.master_module_label', 'Master type')}
                        </label>
                        {/* Modules come from the API. */}
                        <SearchableSelect
                            testId="master-data-translation-module-select"
                                variant="form"
                            value={selectedModule}
                            onChange={setSelectedModule}
                            options={(masterModules || []).map((m) => ({ value: m.name, label: m.label }))}
                            placeholder={masterModules.length === 0
                                ? t('translationpage.loading', 'Loading...')
                                : t('translationpage.select_module', 'Select a module')}
                            searchPlaceholder={t('common.search', 'Search…')}
                            className="w-full sm:w-64"
                        />
                    </div>

                    <div data-testid="master-data-translation-search-field-container">
                        <label className="block text-xs font-semibold text-gray-600 mb-1" data-testid="master-data-translation-search-label">
                            {t('translationpage.search_label', 'Search')}
                        </label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-testid="master-data-translation-search-icon" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder={t('translationpage.search_placeholder', 'Search key or value...')}
                                className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-[#4ABDBD] focus:outline-none text-sm w-full sm:w-64"
                                data-testid="master-data-translation-search-input"
                            />
                        </div>
                    </div>

                    <div className="flex items-end">
                        <button
                            type="button"
                            onClick={handleRefresh}
                            disabled={masterTranslationLoading}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:text-[#4ABDBD] hover:border-[#4ABDBD]/50 bg-white transition-all disabled:opacity-50 cursor-pointer"
                            data-testid="master-data-translation-refresh-button"
                        >
                            <RefreshCw className={`w-4 h-4 ${masterTranslationLoading ? 'animate-spin' : ''}`} data-testid="master-data-translation-refresh-icon" />
                            {t('translationpage.refresh', 'Refresh')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-testid="master-data-translation-table-container">
                {masterTranslationLoading ? (
                    <div className="py-16" data-testid="master-data-translation-loading"><CommonLoader /></div>
                ) : orderedLanguages.length === 0 ? (
                    <div className="py-16 text-center text-sm text-gray-500" data-testid="master-data-translation-no-languages">
                        {t(
                            'translationpage.no_languages',
                            'No languages defined. Add languages in the Language tab first.'
                        )}
                    </div>
                ) : filteredRows.length === 0 ? (
                    <div className="py-16 text-center text-sm text-gray-500" data-testid="master-data-translation-no-rows">
                        {selectedModuleMeta
                            ? t('translationpage.no_master_rows', 'No {{label}} entries yet.', { label: selectedModuleMeta.label })
                            : t('translationpage.no_master_rows_generic', 'No entries yet.')}
                    </div>
                ) : (
                    <div className="overflow-x-auto" data-testid="master-data-translation-table-scroll">
                        <table className="min-w-full text-sm" data-testid="master-data-translation-table">
                            <thead className="bg-gradient-to-r from-[#4ABDBD]/10 to-[#3AA8A8]/10 text-gray-700">
                                <tr>
                                    <th className="sticky left-0 bg-[#E9F7F7] z-10 px-4 py-3 text-left font-semibold min-w-[220px]" data-testid="master-data-translation-key-header">
                                        {t('translationpage.key_column', 'Key')}
                                    </th>
                                    {orderedLanguages.map((lang) => (
                                        <th
                                            key={lang.code}
                                            className="px-4 py-3 text-left font-semibold min-w-[220px]"
                                            data-testid={`master-data-translation-lang-header-${lang.code}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span data-testid={`master-data-translation-lang-name-${lang.code}`}>{lang.name}</span>
                                                <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-white border border-gray-200 rounded-md text-gray-500" data-testid={`master-data-translation-lang-code-${lang.code}`}>
                                                    {lang.code}
                                                </span>
                                                {lang.code === sourceLcode && (
                                                    <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-[#4ABDBD]/15 text-[#2D8A8A] rounded-md" data-testid={`master-data-translation-source-tag-${lang.code}`}>
                                                        {t('translationpage.source', 'source')}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                    <th className="px-4 py-3 text-right font-semibold min-w-[110px]" data-testid="master-data-translation-actions-header">
                                        {t('translationpage.actions_column', 'Actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((row) => (
                                    <tr key={row.keyId} className="border-t border-gray-100 hover:bg-gray-50/50" data-testid={`master-data-translation-row-${row.keyId}`}>
                                        <td className="sticky left-0 bg-white z-10 px-4 py-3 align-top">
                                            <div className="font-mono text-xs text-gray-800 break-all" data-testid={`master-data-translation-key-name-${row.keyId}`}>{row.keyName}</div>
                                            <div className="text-[11px] text-gray-400 mt-1" data-testid={`master-data-translation-key-meta-${row.keyId}`}>
                                                {row.entityId} · {row.field}
                                            </div>
                                        </td>
                                        {orderedLanguages.map((lang) => {
                                            const isSource = lang.code === sourceLcode;
                                            const value = getCellValue(row, lang.code);
                                            const dirtyCell = isCellDirty(row.keyId, lang.code);
                                            return (
                                                <td key={lang.code} className="px-4 py-3 align-top">
                                                    <div className="flex items-start gap-2">
                                                        <textarea
                                                            ref={(el) => { inputRefs.current[`${row.keyId}-${lang.code}`] = el; }}
                                                            rows={1}
                                                            readOnly={isSource || !canManage}
                                                            value={value}
                                                            onChange={(e) => handleCellChange(row.keyId, lang.code, e.target.value)}
                                                            onKeyDown={(e) => handleKeyDown(e, row, lang.code)}
                                                            onBlur={() => { if (!isSource && canManage) handleSaveCell(row, lang.code); }}
                                                            placeholder={isSource ? '' : t('translationpage.cell_placeholder', 'Enter translation...')}
                                                            className={`flex-1 resize-y px-3 py-2 border rounded-lg text-sm transition-colors
                                                                ${isSource
                                                                    ? 'bg-gray-50 border-gray-200 text-gray-600 cursor-not-allowed'
                                                                    : dirtyCell
                                                                        ? 'bg-yellow-50 border-yellow-300 focus:ring-2 focus:ring-yellow-300 focus:outline-none'
                                                                        : 'bg-white border-gray-200 focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD] focus:outline-none'
                                                                }`}
                                                            data-testid={`master-data-translation-cell-input-${row.keyId}-${lang.code}`}
                                                        />
                                                        {canManage && !isSource && dirtyCell && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSaveCell(row, lang.code)}
                                                                disabled={masterTranslationSaving}
                                                                className="mt-1 p-1.5 rounded-md bg-[#4ABDBD] text-white hover:bg-[#3AA8A8] disabled:opacity-60 cursor-pointer"
                                                                title={t('translationpage.save', 'Save')}
                                                                data-testid={`master-data-translation-save-button-${row.keyId}-${lang.code}`}
                                                            >
                                                                <Save className="w-3.5 h-3.5" data-testid={`master-data-translation-save-icon-${row.keyId}-${lang.code}`} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                        <td className="px-4 py-3 text-right align-top">
                                            {canManage && (
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteKey(row)}
                                                disabled={masterTranslationSaving}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs font-semibold transition-colors disabled:opacity-50 cursor-pointer"
                                                title={t('translationpage.delete', 'Delete')}
                                                data-testid={`master-data-translation-delete-button-${row.keyId}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" data-testid={`master-data-translation-delete-icon-${row.keyId}`} />
                                                {t('translationpage.delete', 'Delete')}
                                            </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Hint footer */}
            {orderedLanguages.length > 0 && filteredRows.length > 0 && (
                <div className="mt-3 text-xs text-gray-500" data-testid="master-data-translation-hint-text">
                    {t(
                        'translationpage.edit_hint',
                        'Tip: Press Enter to save a cell, Escape to cancel. Cells turn yellow while unsaved.'
                    )}
                </div>
            )}
        </div>
    );
};

export default MasterDataTranslation;
